import React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { CommunityPipeline } from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";
import SectionShell from "../SectionShell";
import {
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import { getPipelineStages } from "../utils";

interface ChangePipelineProps {
  query: UseQueryResult<CommunityPipeline>;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
}

/**
 * Issue funnel from problem discovery to execution coverage.
 */
const ChangePipeline: React.FC<ChangePipelineProps> = ({
  query,
  labels,
  numberFormatter,
}) => {
  return (
    <SectionShell
      title={labels.changePipelineTitle}
      subtitle={labels.changePipelineSubtitle}
      contentClassName="space-y-5"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data ? (
        <>
          <div className="overflow-hidden rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/40 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/20">
            <div className="flex h-4 items-stretch">
              {getPipelineStages(query.data, labels).map((stage, index) => (
                <div
                  key={stage.key}
                  className={
                    index === 0
                      ? "bg-blue-500/70"
                      : index === 1
                        ? "bg-cyan-500/70"
                        : index === 2
                          ? "bg-violet-500/70"
                          : index === 3
                            ? "bg-amber-500/70"
                            : "bg-emerald-500/70"
                  }
                  style={{
                    width: `${Math.max(stage.share * 100, stage.value > 0 ? 10 : 0)}%`,
                  }}
                />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            {getPipelineStages(query.data, labels).map((stage, index) => {
              const tone =
                index === 0
                  ? "from-blue-500/10 to-transparent"
                  : index === 1
                    ? "from-cyan-500/10 to-transparent"
                    : index === 2
                      ? "from-violet-500/10 to-transparent"
                      : index === 3
                        ? "from-amber-500/10 to-transparent"
                        : "from-emerald-500/10 to-transparent";

              return (
                <div
                  key={stage.key}
                  className={`rounded-2xl border border-eoe-border/80 bg-gradient-to-br ${tone} p-4 dark:border-eoe-dark-border/80`}
                >
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {stage.label}
                      </p>
                      <p className="text-3xl font-semibold tracking-tight text-eoe-text dark:text-eoe-dark-text">
                        {numberFormatter.format(stage.value)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        <span>{labels.pipelineOfTotal}</span>
                        <span>{Math.round(stage.share * 100)}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-eoe-border/70 dark:bg-eoe-dark-border/70">
                        <div
                          className="h-full rounded-full bg-blue-600 dark:bg-blue-400"
                          style={{ width: `${Math.round(stage.share * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {stage.conversionFromPrevious === null
                        ? null
                        : `${labels.pipelineConversion}: ${Math.round(stage.conversionFromPrevious * 100)}%`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <SectionLoadingState message={labels.loading} />
      )}
    </SectionShell>
  );
};

export default ChangePipeline;

